/**
 * rvrn-ride-preview-form.js — 产品页「Let's See It on Your Ride」线索表单
 *
 * 这份表单原来在预览页里（RuoYi: static/wheel/product-3d-preview/product-ride-preview.html
 * 的 <div id="mo-ride-preview">，页面加载后自动弹出、强制填写）。2026-09-29 迁到产品页：
 *
 *   产品主图右上角 AI Visualize 按钮（assets/product-options.js）
 *     → window.RvrnRidePreviewForm.open(...)  弹出本表单
 *     → 顾客填完 Name / Email / Phone / Vehicle，点 Continue to AI Preview
 *     → onSubmit(lead) 由调用方把车辆数据并入参数，以 <form method="post" target="_blank">
 *       提交到后台配置的 3D AI Generation URL —— 新标签页打开预览页
 *       （RuoYi 侧 ProductRidePreviewEntryFilter 把 POST 归一成 query 串再渲染页面），
 *       同时调用方 fire-and-forget 落库 + 订阅（见 product-options.js#aiVisualizePersistLead）。
 *
 * 调用契约（唯一调用方：assets/product-options.js#onAiVisualizeClick）：
 *   RvrnRidePreviewForm.open({
 *     image:       'https://cdn.shopify.com/.../wheel.jpg',  // 左卡参考轮毂图，空 = 显示占位转圈
 *     productName: 'RV-DR08 Brushed 2-Piece',                // 右卡商品名，空 = 右卡隐藏
 *     vehicle:     '2026 Corvette C8 Z06',                   // 预填（商品选项 / 本地车型表）
 *     onSubmit:    function (lead) { ... }                   // 校验通过后回调，lead 见下方 payload
 *   })  → 返回 true = 表单已接管（调用方不要再直接 POST）；false = 表单不可用（调用方走原逻辑）
 *
 * onSubmit 的 lead：
 *   { name, email, phone,              // 用户填写
 *     vehicle,                         // 用户填写的车辆原文（"2026 Corvette C8 Z06"）
 *     year, make, model }              // parseVehicle 拆出来的三段（拆不出＝空串）
 *
 * 关闭行为（弹窗内部右上角 X）：
 *   · 点 X / 点遮罩 / 按 ESC → 收起弹窗，回到产品页面（顾客可继续浏览商品，也能再点按钮重新打开）；
 *   · 点 Continue to AI Preview 且提交出去 → 同样收起弹窗（POST 打开的是新标签页，本页回到产品页面）。
 * 与迁移前预览页弹窗的差别：只去掉了 dismissible:false 的强制模式 —— 产品页上这张表单是
 * AI Visualize 按钮的可选项，不再是必须填完才能看页面的门禁。
 * 字段、文案、校验提示、卡片布局都与预览页那一份逐字一致。
 *
 * 健壮性约定（"点了没反应"这类静默失败一律不允许）：
 *   · 元素全部在调用时用 document.getElementById 取（不缓存到模块加载时刻），
 *     脚本顺序 / 局部重渲染都不会让监听器或元素引用落空；
 *   · 提交走双通道：按钮 click（捕获阶段，自己 preventDefault，不依赖浏览器默认行为是否被主题 JS 取消）
 *     + 表单 submit / 回车。两条路都进 runSubmit()，带同步重入保护；
 *   · **不依赖 <form> 元素存在**：弹窗若被渲染进主题的 add-to-cart <form>，嵌套的 <form id="rp-form">
 *     会被 HTML 解析器整条丢弃（DOM 里没有它，按钮还会变成加购表单的提交按钮 —— 曾经"点了没反应"的根因）。
 *     校验/清错按 id 定位、提交走 click 与回车通道，表单元素在不在都能跑；Snippet 侧仍应把
 *     {% render 'rvrn-ride-preview-form' %} 放在 {% form 'product' %} 之外（三个产品模板已如此）；
 *   · 任何走不下去的分支都把话说出来：字段错误落红、其余情况写进弹窗里的 #rp-status 状态行，
 *     同时在 console 打 [RidePreviewForm] 日志；
 *   · 载入时自检一次（元素是否齐、组件 CSS 是否生效），结果打日志 —— 线上排查"点了没反应"用。
 */
(function () {
  'use strict';

  var MODAL_ID = 'mo-ride-preview';
  var FORM_ID = 'rp-form';
  var SUBMIT_ID = 'rp-submit-btn';
  var CLOSE_ID = 'rp-modal-close';
  var STATUS_ID = 'rp-status';
  /** 常见「两个单词组成的品牌」：只按空格切分会把 "Alfa Romeo" 误判成 make=Alfa / model=Romeo */
  var MULTI_WORD_MAKES = ['alfa romeo', 'aston martin', 'land rover', 'range rover', 'rolls royce',
    'mercedes benz', 'am general'];

  /** open() 传入的回调（每次打开重置；close() 后为 null） */
  var onSubmitCb = null;
  /** runSubmit 同步重入保护（click 与 submit 两条通道别跑两遍） */
  var running = false;

  /** 元素一律现取现用：脚本顺序 / 局部重渲染都不会留下失效引用 */
  function el(id) {
    return document.getElementById(id);
  }

  function modalEl() {
    return el(MODAL_ID);
  }

  function val(id) {
    var node = el(id);
    return node ? String(node.value || '').trim() : '';
  }

  /** 只补空字段：用户在本次会话里已经改过的内容（关掉弹窗又打开）不会被预填覆盖 */
  function prefillValue(id, v) {
    var node = el(id);
    if (node && v && !node.value) node.value = v;
  }

  function attr(name) {
    var m = modalEl();
    return (m && m.getAttribute) ? String(m.getAttribute(name) || '') : '';
  }

  /* ---------- 挂到 body ----------
   * 弹窗标记由 snippets/rvrn-ride-preview-form.liquid 就近插在商品模板里（跟着 #product-options-root 走），
   * 但 position:fixed 的遮罩一旦祖先上有 transform / filter / will-change 就会被关进那个祖先，
   * 也被父容器的 overflow 裁掉。这里统一搬到最后：Liquid 只负责加载，层级交给 body。
   */
  (function detachToBody() {
    try {
      var modal = modalEl();
      var body = document.body;
      if (modal && body && modal.parentNode !== body && typeof body.appendChild === 'function') {
        body.appendChild(modal);
      }
    } catch (e) { /* 沙箱/受限环境：搬不动就保持原位，功能的其余部分不受影响 */ }
  })();

  /* ---------- 状态 / 错误提示 ---------- */
  /** 弹窗里的状态行：提交中、失败、未接线都要有可见反馈 */
  function setStatus(message, isError) {
    var box = el(STATUS_ID);
    if (!box) return;
    box.textContent = message || '';
    if (box.classList) {
      if (isError) box.classList.add('dr-status--error');
      else box.classList.remove('dr-status--error');
    }
    box.hidden = !message;
  }

  /**
   * 四个必填项（Year/Make/Model/Trim 都写在 Vehicle 一个字段里）。
   * 校验/清错一律按 id 直接定位，**不经过 <form> 元素、也不依赖 querySelectorAll** ——
   * 弹窗万一又被套进别的 <form>（HTML 解析器会丢弃嵌套的 form 元素、#rp-form 从 DOM 里消失），
   * 校验与提交依然照常工作。
   */
  var FIELDS = [
    { field: 'rp-field-name', input: 'rp-name', error: 'rp-error-name',
      message: 'Please enter your name.' },
    { field: 'rp-field-email', input: 'rp-email', error: 'rp-error-email',
      message: 'Please enter a valid email address.' },
    { field: 'rp-field-phone', input: 'rp-phone', error: 'rp-error-phone',
      message: 'Please enter your phone number.' },
    { field: 'rp-field-vehicle', input: 'rp-vehicle', error: 'rp-error-vehicle',
      message: 'Please enter your vehicle year, make, model and trim.' }
  ];

  function clearErrors() {
    FIELDS.forEach(function (f) {
      var field = el(f.field);
      if (field && field.classList) field.classList.remove('dr-field--error');
      var input = el(f.input);
      if (input && input.classList) input.classList.remove('dr-input--error');
      var err = el(f.error);
      if (err) err.textContent = '';
    });
  }

  /** 标红 + 写提示（输入框获焦时自动收掉这一条） */
  function markError(f, input) {
    var field = el(f.field);
    if (field && field.classList) field.classList.add('dr-field--error');
    var err = el(f.error);
    if (err) err.textContent = f.message;
    if (input) {
      input.classList.add('dr-input--error');
      input.addEventListener('focus', function clear() {
        if (field && field.classList) field.classList.remove('dr-field--error');
        input.classList.remove('dr-input--error');
        if (err) err.textContent = '';
        input.removeEventListener('focus', clear);
      }, { once: true });
    }
  }

  function validateFields() {
    var ok = true;
    FIELDS.forEach(function (f) {
      var input = el(f.input);
      if (!input || !String(input.value || '').trim()) { markError(f, input); ok = false; }
    });
    return ok;
  }

  /* ---------- 车辆串解析（口径与预览页 ride-preview-form.js#parseVehicle 一致） ---------- */
  /**
   * 解析「年份 + Make + Model & trim」。容错口径：
   *   · 年份：文本中独立成词的 1900–2099 四位数字，取第一个；"MY2026 ..." 的 MY 前缀先剥掉。找不到就留空。
   *   · 显式分隔符（| · •）优先：前半 = Make，后半 = Model & trim（逗号/斜杠不切 —— "Corvette C8/Z06"、
   *     "Silverado, Crew Cab" 里的逗号和斜杠属于型号本身）。
   *   · 无分隔符：命中多词品牌表 → 该品牌为 Make；否则第一个 token 为 Make，其余为 Model & trim。
   * 绝不抛异常：任何输入都返回 { year, make, model }（可能是空串）。
   */
  function parseVehicle(text) {
    var out = { year: '', make: '', model: '' };
    var s = String(text == null ? '' : text).replace(/\s+/g, ' ').trim();
    if (!s) return out;
    s = s.replace(/^(?:my|model\s+year)\s*(?=\d{4}\b)/i, '');          // "MY2026 Corvette C8" -> "Corvette C8"
    var m = s.match(/(?:^|\s)((?:19|20)\d{2})(?=\s|$)/);
    if (m) {
      out.year = m[1];
      s = (s.slice(0, m.index) + ' ' + s.slice(m.index + m[0].length)).replace(/\s+/g, ' ').trim();
    }
    if (!s) return out;
    var parts = s.split(/\s*[|·•]\s*/).filter(function (p) { return !!p.trim(); });
    if (parts.length > 1) {
      out.make = parts[0].trim();
      out.model = parts.slice(1).join(' ').trim();
      return out;
    }
    var lower = s.toLowerCase();
    for (var i = 0; i < MULTI_WORD_MAKES.length; i++) {
      var mk = MULTI_WORD_MAKES[i];
      // 表项之后必须是字符串结尾或分隔符，避免 "Mercedes Benzine" 被当成 Mercedes Benz
      if (lower.indexOf(mk) === 0 && (s.length === mk.length || /[\s-]/.test(s.charAt(mk.length)))) {
        out.make = s.slice(0, mk.length).trim();
        out.model = s.slice(mk.length).replace(/^[\s-]+/, '').trim();
        return out;
      }
    }
    var tokens = s.split(' ').filter(Boolean);
    out.make = tokens[0] || '';
    out.model = tokens.slice(1).join(' ');
    return out;
  }

  /* ---------- 内容（左卡参考图 / 右卡商品名） ---------- */
  function renderCards(imageUrl, productName) {
    var url = String(imageUrl || '').trim();
    var img = el('rp-design-img');
    var loading = el('rp-design-loading');
    if (img) {
      if (url) { img.src = url; img.hidden = false; }
      else { img.removeAttribute('src'); img.hidden = true; }
    }
    if (loading) loading.hidden = !!url;

    var name = String(productName || '').trim();
    var nameBox = el('rp-product-name');
    var productCard = el('rp-product-card');
    if (nameBox) nameBox.textContent = name;
    if (productCard) productCard.hidden = !name;
  }

  /** Liquid 注入的登录态资料（只补空字段，用户已经改过的不覆盖） */
  function prefillContact() {
    prefillValue('rp-name', attr('data-customer-name'));
    prefillValue('rp-email', attr('data-customer-email'));
    prefillValue('rp-phone', attr('data-customer-phone'));
  }

  /** 背景滚动锁：弹窗打开时冻结页面滚动，关闭时还原 */
  function lockScroll(on) {
    try {
      document.documentElement.style.overflow = on ? 'hidden' : '';
    } catch (e) { /* noop */ }
  }

  /* ---------- open / close ---------- */
  /**
   * @param {Object} opts 见文件头调用契约
   * @returns {boolean} 表单是否已打开（false = 页面里没这张表单，调用方走原逻辑）
   */
  function open(opts) {
    var modal = modalEl();
    if (!modal) return false;                 // 主题没渲染 snippet（或后台没配 URL）
    opts = opts || {};
    onSubmitCb = (typeof opts.onSubmit === 'function') ? opts.onSubmit : null;

    var submitBtn = el(SUBMIT_ID);
    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Continue to AI Preview'; }
    clearErrors();
    setStatus('');
    prefillContact();
    if (opts.vehicle) prefillValue('rp-vehicle', opts.vehicle);
    renderCards(opts.image, opts.productName);

    modal.classList.add('is-open');
    lockScroll(true);

    // 打开即把光标放到第一个还没填的必填项（登录顾客 Name/Email 已预填 → 直接落在 Vehicle 上）
    ['rp-name', 'rp-email', 'rp-phone', 'rp-vehicle'].some(function (id) {
      var node = el(id);
      if (node && !node.value) { try { node.focus(); } catch (e) { /* noop */ } return true; }
      return false;
    });
    console.log('[RidePreviewForm] opened (debug: window.RvrnRidePreviewForm)');
    return true;
  }

  function close() {
    var modal = modalEl();
    if (modal && modal.classList) modal.classList.remove('is-open');
    lockScroll(false);
    onSubmitCb = null;
  }

  /* ---------- 提交 ---------- */
  /** 校验 + 交给调用方 POST；这个函数只负责"说出发生了什么" */
  function doSubmit() {
    clearErrors();
    setStatus('');

    if (!validateFields()) {
      console.warn('[RidePreviewForm] submit blocked by validation');
      return;
    }

    var vehicle = val('rp-vehicle');
    var ymm = parseVehicle(vehicle);
    var lead = {
      name: val('rp-name'),
      email: val('rp-email'),
      phone: val('rp-phone'),
      vehicle: vehicle,
      year: ymm.year,
      make: ymm.make,
      model: ymm.model
    };

    // 未接线（没有调用方回调）：说清楚，别静默关掉弹窗
    if (!onSubmitCb) {
      console.warn('[RidePreviewForm] no onSubmit callback — form was not opened via the AI Visualize button');
      setStatus('This form is not connected yet. Please close it and open it again from the “AI Visualize” button on the product image.', true);
      return;
    }

    var submitBtn = el(SUBMIT_ID);
    var originalText = submitBtn ? submitBtn.textContent : '';
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Opening your preview…'; }
    setStatus('Opening your preview in a new tab…', false);
    console.log('[RidePreviewForm] submit -> onSubmit', lead);

    // 目标效果不变：交给调用方 POST 到预览页（新标签页承载）。+ 提交成功即关闭本弹窗：
    // 弹窗先收起、再回到产品页面上，用户不必自己点右上角的 X。
    var submitted = true;
    try {
      submitted = onSubmitCb(lead) !== false;
    } catch (err) {
      console.error('[RidePreviewForm] onSubmit callback error:', err);
      submitted = false;
    }

    if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = originalText; }

    if (submitted) {
      close();                                  // 提交出去了 → 收起自己的弹窗，回到产品页面
    } else {
      console.warn('[RidePreviewForm] submit was not accepted, keeping the form open for a retry');
      setStatus('We could not open your preview just now. Please try again.', true);
    }
  }

  /** 单入口 + 同步重入保护：click 通道与 submit 通道不会跑两遍 */
  function runSubmit() {
    if (running) return;
    running = true;
    try {
      doSubmit();
    } finally {
      running = false;
    }
  }

  window.RvrnRidePreviewForm = {
    open: open,
    close: close,
    /** "Year Make Model & trim" -> { year, make, model }（与预览页同一份口径，QA 可直接调用） */
    parseVehicle: parseVehicle,
    /** 手动触发一次提交（排查用：window.RvrnRidePreviewForm.submit()） */
    submit: runSubmit
  };

  /* ---------- wiring ----------
   * 全部用 document 级委托 + 现取现用，避免"模块加载时元素还没到位"导致的哑火。
   * 按钮 click 在捕获阶段自己 preventDefault：不依赖浏览器的默认提交行为是否被主题 JS 取消
   * （取消就只能靠 submit 事件，那条路一旦被掐掉就是"点了没反应"）。
   */
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t) return;
    var modal = modalEl();
    if (modal && (t === modal || t.id === MODAL_ID)) { close(); return; }   // 点遮罩关闭
    if (!t.closest) return;
    if (t.closest('#' + CLOSE_ID)) { close(); return; }                     // 右上角 X
    if (t.closest('#' + SUBMIT_ID)) {                                       // Continue to AI Preview
      if (e.preventDefault) e.preventDefault();
      runSubmit();
    }
  }, true);

  document.addEventListener('submit', function (e) {
    // 回车提交走这里（按钮点击已在上面被 preventDefault，不会重复）
    var t = e.target;
    if (!t || t.id !== FORM_ID) return;
    if (e.preventDefault) e.preventDefault();
    runSubmit();
  }, true);

  document.addEventListener('keydown', function (e) {
    var modal = modalEl();
    var opened = !!(modal && modal.classList && modal.classList.contains('is-open'));
    if (e.key === 'Escape') {
      if (opened) close();
      return;
    }
    // 回车提交：不依赖 <form> 是否存在（嵌套 form 被解析器丢弃时，回车不会触发任何 submit 事件）
    if (e.key === 'Enter' && opened) {
      var t = e.target;
      var isInput = t && t.id && FIELDS.some(function (f) { return f.input === t.id; });
      if (!isInput) return;
      if (e.preventDefault) e.preventDefault();
      runSubmit();
    }
  });

  /* ---------- 载入自检（线上排查"点了没反应"） ---------- */
  (function selfCheck() {
    var modal = modalEl();
    if (!modal) {
      console.warn('[RidePreviewForm] #' + MODAL_ID + ' 不在页面里：AI Visualize 按钮会退回一步 POST'
        + '（主题没渲染 snippets/rvrn-ride-preview-form.liquid，或后台没配 3D AI Generation URL）');
      return;
    }
    if (!el(FORM_ID)) {
      // 曾经踩过：弹窗被渲染在主题的 add-to-cart <form> 里，嵌套 form 会被 HTML 解析器整条丢弃。
      // 现在功能不受影响（提交走按钮 click / 回车通道），但 DOM 语义上应该把 render 挪到 form 之外。
      console.warn('[RidePreviewForm] #' + FORM_ID + ' 不在 DOM 里 —— 弹窗的 <form> 被解析器丢弃了'
        + '（多半被套进了主题 add-to-cart 的 <form>）；提交仍可用，建议把 snippet 的 render 移到'
        + " {% form 'product' %} 之外");
    }
    var missing = [SUBMIT_ID, 'rp-name', 'rp-email', 'rp-phone', 'rp-vehicle']
      .filter(function (id) { return !el(id); });
    if (missing.length) console.warn('[RidePreviewForm] 弹窗里缺元素:', missing.join(', '));
    try {
      var gcs = (typeof getComputedStyle === 'function') ? getComputedStyle : window.getComputedStyle;
      if (gcs && gcs(modal).display !== 'none') {
        console.warn('[RidePreviewForm] 组件 CSS 似乎没生效（#' + MODAL_ID + ' 初始 display = '
          + gcs(modal).display + '，应为 none）—— 检查 assets/component-rvrn-ride-preview-form.css 是否已上传');
      }
    } catch (e) { /* noop */ }
    console.log('[RidePreviewForm] ready', {
      form: !!el(FORM_ID), submit: !!el(SUBMIT_ID), customerName: attr('data-customer-name')
    });
  })();
})();
